import { useEffect, useRef } from "react";

/** Two-row "tracker" layout: A W S E D F T G Y H U J K O L P ; → semitones 0–16. */
export const KEY_TO_SEMITONE: Record<string, number> = {
  KeyA: 0, KeyW: 1, KeyS: 2, KeyE: 3, KeyD: 4, KeyF: 5, KeyT: 6, KeyG: 7, KeyY: 8, KeyH: 9, KeyU: 10, KeyJ: 11,
  KeyK: 12, KeyO: 13, KeyL: 14, KeyP: 15, Semicolon: 16,
};
export const KEY_LABEL: Record<string, string> = Object.fromEntries(Object.keys(KEY_TO_SEMITONE).map((k) => [k, k === "Semicolon" ? ";" : k.slice(3)]));

function isTyping(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  return !!el?.tagName && (el.isContentEditable || ["INPUT", "SELECT", "TEXTAREA"].includes(el.tagName));
}

/**
 * Play notes from the computer keyboard. Disabled while typing in a field
 * (AGENT.md §17). Z/X shift octave. Releases notes on unmount.
 */
export function useComputerKeyboard(baseNote: number, onNoteOn: (n: number) => void, onNoteOff: (n: number) => void, onOctave: (delta: number) => void) {
  const down = useRef(new Map<string, number>());
  const cb = useRef({ onNoteOn, onNoteOff, onOctave, baseNote });
  useEffect(() => { cb.current = { onNoteOn, onNoteOff, onOctave, baseNote }; });

  useEffect(() => {
    const held = down.current;
    const kd = (e: KeyboardEvent) => {
      if (e.repeat || e.metaKey || e.ctrlKey || e.altKey || isTyping(e.target)) return;
      if (e.code === "KeyZ") { cb.current.onOctave(-1); return; }
      if (e.code === "KeyX") { cb.current.onOctave(1); return; }
      const semi = KEY_TO_SEMITONE[e.code];
      if (semi === undefined || held.has(e.code)) return;
      const note = cb.current.baseNote + semi;
      held.set(e.code, note);
      cb.current.onNoteOn(note);
    };
    const ku = (e: KeyboardEvent) => {
      const note = held.get(e.code);
      if (note === undefined) return;
      held.delete(e.code);
      cb.current.onNoteOff(note);
    };
    window.addEventListener("keydown", kd);
    window.addEventListener("keyup", ku);
    return () => {
      window.removeEventListener("keydown", kd);
      window.removeEventListener("keyup", ku);
      for (const n of held.values()) cb.current.onNoteOff(n);
      held.clear();
    };
  }, []);
}
